import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'admin' | 'user';
  requiredCapability?: 'canUseTeaching' | 'canManageUsers' | 'canManageContentSources';
  fallback?: React.ReactNode;
}

/**
 * ProtectedRoute component that enforces authentication and authorization
 * 
 * Provides capability-based access control for routes, checking both legacy roles
 * and modern capability system. Supports impersonation-aware authorization.
 * 
 * @param children - The component(s) to render if access is granted
 * @param requiredRole - Legacy role requirement ('admin' | 'user')
 * @param requiredCapability - Modern capability requirement (more specific than roles)
 * @param fallback - Optional fallback component to show while loading
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole = 'user',
  requiredCapability,
  fallback 
}) => {
  const { user, isLoading, isAuthenticated } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking auth
  if (isLoading) {
    return fallback || (
      <div className="auth-loading">
        <div className="spinner-large"></div>
        <p>Checking authentication...</p>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check capability requirements first (more specific)
  if (requiredCapability) {
    const hasCapability = user?.capabilities?.[requiredCapability] === true;
    if (!hasCapability) {
      const capabilityNames = {
        canUseTeaching: 'Teaching System',
        canManageUsers: 'User Management', 
        canManageContentSources: 'Content Source Management'
      };
      
      return (
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You don't have permission to access {capabilityNames[requiredCapability]}.</p>
          <p>Your current role: {user?.userRole || 'Unknown'}</p>
          {user?.impersonation?.isImpersonating && (
            <p className="text-sm text-gray-600">
              Currently impersonating as: {user.impersonation.effectiveRole}
            </p>
          )}
        </div>
      );
    }
  }
  
  // Fallback to legacy role checking (for backward compatibility)
  if (requiredRole === 'admin' && user?.role !== 'admin') {
    return (
      <div className="access-denied">
        <h2>Access Denied</h2>
        <p>You need admin privileges to access this page.</p>
        <p>Your current role: {user?.role}</p>
      </div>
    );
  }

  // All checks passed - render the protected content
  return <>{children}</>;
};

export default ProtectedRoute;